import { Accordion, ModalContext, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { v4 as uuidv4 } from 'uuid';

import { SinacorAccount } from '../../../store/customer/types';

import { AccountColumnsHeader } from './AccountColumnsHeader';
import { AccountRow } from './AccountRow';
import { SelectionTrigger } from './SelectionTrigger';

const modalItemStyle = {
  alignSelf: 'stretch' as const,
  borderRadius: Utils.Token.ToInt(BoxRadius, 'sm'),
  paddingHorizontal: Utils.Token.ToInt(Spacing, 'inline.nano'),
  paddingVertical: Utils.Token.ToInt(Spacing, 'inset.quarck'),
};

interface AccountsSelectionModalProps {
  accounts: SinacorAccount[];
  selectedAccount: SinacorAccount;
  onSelect: (sinacorAccount: string) => void;
}

export const AccountsSelectionModal: React.FC<AccountsSelectionModalProps> = ({
  accounts,
  selectedAccount,
  onSelect,
}) => {
  const modalId = useMemo(() => uuidv4(), []);
  const { appendModal, removeModal } = useContext(ModalContext);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);

  const handleRemoveModal = useCallback(() => {
    removeModal(modalId);
  }, [modalId, removeModal]);

  const handleClose = useCallback(() => {
    setIsOptionsModalOpen(false);
    handleRemoveModal();
  }, [handleRemoveModal]);

  const handleSelectAccount = useCallback(
    (sinacorAccount: string) => {
      onSelect(sinacorAccount);
      handleClose();
    },
    [handleClose, onSelect]
  );

  const optionList = useMemo(() => {
    return (
      <VStack space="stack.quarck" borderRadius={BoxRadius.sm} py="inset.quarck">
        <View style={modalItemStyle}>
          <AccountColumnsHeader />
        </View>
        {accounts.map((account) => (
          <Pressable
            key={`${account.custodyAccountNormalized}-${account.sinacorAccountNormalized}`}
            onPress={() => handleSelectAccount(account.sinacorAccountNormalized)}
            style={({ pressed }) => [
              modalItemStyle,
              pressed && { backgroundColor: Color['brand.tertiary.lightest'] },
            ]}>
            <AccountRow
              custodyAccountNormalized={account.custodyAccountNormalized}
              sinacorAccountNormalized={account.sinacorAccountNormalized}
              isSelected={
                account.sinacorAccountNormalized === selectedAccount.sinacorAccountNormalized
              }
            />
          </Pressable>
        ))}
      </VStack>
    );
  }, [accounts, handleSelectAccount, selectedAccount.sinacorAccountNormalized]);

  useEffect(() => {
    if (isOptionsModalOpen) {
      appendModal(
        modalId,
        <Modal transparent>
          <Accordion.Expanded onClose={handleClose} maxContentHeight="70vh">
            {optionList}
          </Accordion.Expanded>
        </Modal>
      );
    } else {
      handleRemoveModal();
    }

    return () => {
      handleRemoveModal();
    };
  }, [appendModal, handleClose, handleRemoveModal, isOptionsModalOpen, modalId, optionList]);

  return (
    <>
      <SelectionTrigger
        onPress={() => setIsOptionsModalOpen((currentState) => !currentState)}
        chevronName={isOptionsModalOpen ? 'ChevronUp' : 'ChevronDown'}
        account={selectedAccount}
      />
    </>
  );
};
